import { DSContractListing } from "@myth-finance/dualstake-ts-sdk";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn, formatNumber, preventDefault, shortenNumber } from "@/lib/utils";
import { Link } from "react-router-dom";
import { StoplightIndicator } from "@/components/stoplight-indicator";
import { Token } from "@/components/token";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import { Sprout, User, Wrench } from "lucide-react";
import { useWallet } from "@txnlab/use-wallet";
import { twMerge } from "tailwind-merge";
import { useAlgoAcctInfo, useBalance } from "@/hooks/use-algo-account";
import { DSContractListingExtd, DSFarmAndApr } from "@/lib/types";
import { cx } from "class-variance-authority";

interface ListingCardProps {
  listing: DSContractListingExtd;
  farmAndApr?: DSFarmAndApr;
  small?: boolean;
  className?: string;
  onClick?: () => void;
}

export function ListingCard({
  listing,
  farmAndApr,
  small,
  className,
  onClick,
}: ListingCardProps) {
  const asaId = Number(listing.asaId);
  const hasFarm = !!farmAndApr?.farm;
  const aprBreakdown = farmAndApr?.apr;
  const hoverBorder = getHighlightClasses(asaId, "hover:border");
  const hoverText = getHighlightClasses(asaId, "group-hover:text");

  const aprClass =
    listing.isOnline && listing.incentiveEligible && listing.staked >= 3e10
      ? hoverText
      : `${hoverText} line-through`;

  return (
    <Link
      to={`/dualSTAKE/${listing.lstName}`}
      onClick={onClick}
      className={className}
    >
      <Card
        className={cn(
          `min-w-[300px] group rounded-tl-[48px] cursor-pointer  ${hoverBorder} hover:border-2 `,
          small
            ? "rounded-bl-[48px] hover:-m-[1px]"
            : `hover:scale-105 hover:-mt-1 transition-all`
        )}
      >
        <CardHeader className="flex-row w-full gap-4 items-start p-0">
          <Token
            glowing={false}
            className={cn(
              "-left-[1px] -top-[1px]",
              small
                ? "m-[-2px]"
                : "group-hover:-left-[2px] group-hover:-top-[2px]"
            )}
            variant="md"
            leftAssetId={asaId}
            rightAssetId={0}
          />
          <div className={cx("flex flex-col w-full", small ? "" : "pt-1")}>
            <div className="flex items-center w-full">
              <CardTitle className={`text-lg`}>{listing.lstName}</CardTitle>
            </div>
            <CardDescription
              className={`pr-2 mt-1 group-hover:text-foreground`}
            >
              ALGO+<span className={hoverText}>{listing.asaUnitName}</span>{" "}
              staking
            </CardDescription>
            <div className="flex mt-1 items-center justify-between pr-2">
              <div className="flex items-center gap-1">
                {listing.isOnline &&
                listing.incentiveEligible &&
                listing.staked > 3e10 ? null : (
                  <StoplightIndicator
                    sunset={listing.sunset}
                    online={listing.isOnline}
                    eligible={listing.incentiveEligible}
                    className="py-0 mt-[2px] -ml-2"
                    value={listing.staked}
                    iconSize={18}
                  />
                )}
                <div className="mt-[1px] text-sm mr-1">
                  {aprBreakdown?.netBaseAprBps ? (
                    <span className={aprClass}>
                      {formatNumber(
                        Number(aprBreakdown?.netBaseAprBps) +
                          Number(aprBreakdown?.farmAprBps),
                        2,
                        1
                      )}
                      % APR
                    </span>
                  ) : null}
                  {listing.sunset ? <span className="text-orange-500">Sunset</span> : null}
                </div>
                {hasFarm && !small ? (
                  <FarmIndicator
                    listing={listing}
                    className="text-green-500"
                    iconSize={18}
                  />
                ) : null}
                <OwnStakeIndicator
                  tooltipEnabled={!small}
                  listing={listing}
                  iconSize={18}
                />
                {small ? null : (
                  <>
                    <UpgradeIndicator listing={listing} iconSize={18} />
                  </>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        {small ? null : (
          <CardContent className="flex flex-col w-full justify-between items-start p-4">
            <div className={`text-sm text-muted-foreground ${hoverText}`}>
              Staked
            </div>
            <div>
              {shortenNumber(Number(listing.staked) / 10 ** 6)} ALGO
              <>
                {" "}
                +{" "}
                <span>
                  {shortenNumber(
                    Number(listing.asaBalance) / 10 ** listing.asaDecimals
                  )}{" "}
                  {listing.asaUnitName}
                </span>
              </>
            </div>
          </CardContent>
        )}
      </Card>
    </Link>
  );
}

function OwnStakeIndicator({
  tooltipEnabled = true,
  listing,
  iconSize,
  className,
}: {
  tooltipEnabled: boolean;
  listing: DSContractListing;
  iconSize: number;
  className?: string;
}) {
  const { activeAddress } = useWallet();
  const { data: accountInfo } = useAlgoAcctInfo(activeAddress);
  const lstBalance = Math.max(useBalance(listing.lstId, accountInfo), 0);
  const asaBalance = Math.max(useBalance(listing.asaId, accountInfo), 0);

  const protesting = listing.userProtestingStake;
  const isHolder = protesting > 0n || lstBalance > 0;
  const isAsaHolder = asaBalance > 0;
  const bgClassName = isHolder
    ? "bg-green-600 dark:bg-green-500"
    : "bg-orange-600 dark:bg-yellow-500";
  const fgClassName = isHolder
    ? "text-green-500"
    : "text-orange-600 dark:text-yellow-500";
  return isHolder || isAsaHolder ? (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={!tooltipEnabled ? false : undefined}>
        <TooltipTrigger
          className={twMerge(
            "flex items-center",
            tooltipEnabled ? " cursor-help" : "",
            fgClassName,
            className
          )}
          asChild
          onClick={tooltipEnabled ? preventDefault : undefined}
        >
          <div>
            <User size={iconSize} />
          </div>
        </TooltipTrigger>
        <TooltipContent
          className={cx(
            `${bgClassName} flex flex-col gap-2`,
            tooltipEnabled ? "pointer-events-none" : ""
          )}
        >
          {isHolder ? (
            <>
              <p className="font-bold">Status: {listing.lstName} holder</p>
              <p>It must feel amazing.</p>
            </>
          ) : (
            <>
              <p className="font-bold">Status: {listing.lstName} candidate</p>
              <p>You are a {listing.asaUnitName} holder.</p>
            </>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null;
}

function FarmIndicator({
  iconSize,
  listing,
  className,
}: {
  listing: DSContractListing;
  iconSize: number;
  className?: string;
}) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger
          className="flex items-center cursor-help"
          onClick={preventDefault}
        >
          <div className={className}>
            <Sprout size={iconSize} />
          </div>
        </TooltipTrigger>
        <TooltipContent
          className={`bg-green-600 dark:bg-green-500 pointer-events-none flex flex-col gap-2`}
        >
          <p>
            <strong>Farm: Active</strong>
          </p>
          <p>
            Additional {listing.asaUnitName} rewards are
            <br />
            automatically provided for {listing.lstName}.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function UpgradeIndicator({
  iconSize,
  listing,
  className,
}: {
  listing: DSContractListing;
  iconSize: number;
  className?: string;
}) {
  const { upgrading } = listing;
  return !upgrading ? null : (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger
          className="flex items-center cursor-help"
          onClick={preventDefault}
        >
          <div className={twMerge("text-red-500", className)}>
            <Wrench size={iconSize} />
          </div>
        </TooltipTrigger>
        <TooltipContent
          className={`bg-red-600 pointer-events-none flex flex-col gap-2`}
        >
          <p>
            <strong>Upgrade: Scheduled</strong>
          </p>
          <p>The {listing.lstName} contract has a scheduled code upgrade.</p>
          <p>{listing.lstName} holders can protest this.</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
